interface WebGLContextInfo {
  canvas: HTMLCanvasElement
  gl: WebGLRenderingContext
}

class WebGLContextManager {
  private static instance: WebGLContextManager | null = null
  private contexts: WebGLContextInfo[] = []
  private maxContexts: number

  private constructor(maxContexts: number = 10) {
    this.maxContexts = maxContexts
  }

  /**
   * Retorna a instância única do WebGLContextManager (Singleton).
   */
  public static getInstance(maxContexts: number = 10): WebGLContextManager {
    if (!WebGLContextManager.instance) {
      WebGLContextManager.instance = new WebGLContextManager(maxContexts)
    }
    return WebGLContextManager.instance
  }

  /**
   * Cria um novo contexto WebGL para o canvas fornecido.
   * Se o limite de contextos for atingido, o contexto mais antigo será perdido.
   *
   * @param {HTMLCanvasElement} canvas - O elemento canvas para criar o contexto WebGL.
   * @returns {WebGLRenderingContext | null} - O contexto WebGL ou null em caso de erro.
   */
  public createContext(canvas: HTMLCanvasElement): WebGLRenderingContext | null {
    // Verifica se o contexto já existe para o canvas
    let existingContext = this.contexts.find(ctx => ctx.canvas === canvas)
    if (existingContext) {
      return existingContext.gl
    }

    // Caso contrário, cria um novo contexto WebGL
    const gl = canvas.getContext('webgl')
    if (!gl) {
      return null
    }

    // Se o número máximo de contextos for atingido, perde o contexto mais antigo
    if (this.contexts.length >= this.maxContexts) {
      const oldestContext = this.contexts.shift()
      if (oldestContext) {
        this.loseContext(oldestContext)
      }
    }

    // Adiciona o novo contexto à lista
    this.contexts.push({ canvas, gl })

    return gl
  }

  /**
   * Cria contextos WebGL para uma lista de elementos canvas.
   *
   * @param {NodeListOf<HTMLCanvasElement>} canvasList - Lista de elementos canvas.
   */
  public createContextsForCanvasList(canvasList: NodeListOf<HTMLCanvasElement>): void {
    canvasList.forEach(canvas => {
      this.createContext(canvas)
    })
  }

  /**
   * Libera um contexto WebGL.
   *
   * @param {WebGLContextInfo} context - O objeto de contexto WebGL a ser liberado (contendo o canvas e o contexto).
   */
  public loseContext(context: WebGLContextInfo): void {
    if (context.gl) {
      const extension = context.gl.getExtension('WEBGL_lose_context')
      if (extension) {
        extension.loseContext()
      }
    }
    // Remove o contexto da lista de contextos ativos
    const index = this.contexts.indexOf(context)
    if (index >= 0) {
      this.contexts.splice(index, 1)
    }
  }

  /**
   * Libera todos os contextos WebGL gerenciados.
   */
  public loseAllContexts(): void {
    for (let context of this.contexts) {
      this.loseContext(context)
    }
    this.contexts = []
  }

  /**
   * Recupera o número atual de contextos WebGL ativos.
   *
   * @returns {number} - O número de contextos WebGL ativos.
   */
  public getActiveContextsCount(): number {
    return this.contexts.length
  }

  /**
   * Recupera o contexto WebGL associado a um canvas específico.
   *
   * @param {HTMLCanvasElement} canvas - O canvas para o qual deseja recuperar o contexto WebGL.
   * @returns {WebGLRenderingContext | null} - O contexto WebGL ou null se não existir.
   */
  public getContextByCanvas(canvas: HTMLCanvasElement): WebGLRenderingContext | null {
    const context = this.contexts.find(ctx => ctx.canvas === canvas)
    return context ? context.gl : null
  }
}

export default WebGLContextManager
